import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Card, Pill } from '@/ui/primitives'
import { DataTable, type Coloana } from '@/ui/data-table'

type Aniversare = {
  id: number; nume: string; telefon: string | null; email: string | null; oras: string | null
  nastere: string | null; urmatoarea: string | null; peste_zile: number
  implineste: number; calatorii: number; ultima_calatorie: string | null
}
type Acoperire = { din_camp: number; din_cnp: number; fara_data: number; total_clienti: number }

const FERESTRE = [7, 30, 90]

const zi = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ro-RO', { day: '2-digit', month: 'long' }) : '—'

const urare = (a: Aniversare) =>
  `La multi ani, ${a.nume.split(' ')[0] ?? ''}! Va dorim un an frumos si multe calatorii. ` +
  `Echipa Intertur va sta la dispozitie oricand doriti sa plecati undeva.`

/** Aniversarile clientilor — „Client reminders" din TravelJoy, fara automatizare:
 *  lista se vede, urarea o trimite omul. Data nasterii vine din campul ei sau din CNP. */
export function Aniversari() {
  const nav = useNavigate()
  const [zile, setZile] = useState(30)

  const { data, isPending } = useQuery({
    queryKey: ['aniversari', zile],
    queryFn: () => api.get<{ aniversari: Aniversare[] }>('/marketing/aniversari', { zile }),
  })
  const acoperire = useQuery({
    queryKey: ['aniversari-acoperire'],
    queryFn: () => api.get<Acoperire>('/marketing/acoperire'),
    staleTime: 10 * 60_000,
  })

  const randuri = data?.aniversari ?? []
  const azi = randuri.filter((a) => a.peste_zile === 0)

  const coloane: Coloana<Aniversare>[] = [
    { cheie: 'cand', antet: 'Cand', sortabil: true, valoare: (a) => a.peste_zile,
      randare: (a) => (a.peste_zile === 0
        ? <Badge tone="good">azi</Badge>
        : <span className="tnum text-[13px]">
            {zi(a.urmatoarea)}<span className="text-muted"> · peste {a.peste_zile} z</span>
          </span>) },
    { cheie: 'nume', antet: 'Client', sortabil: true, valoare: (a) => a.nume,
      randare: (a) => (
        <span>
          <span className="font-medium">{a.nume}</span>
          {a.oras && <span className="block text-[12px] text-muted">{a.oras}</span>}
        </span>
      ) },
    { cheie: 'ani', antet: 'Implineste', sortabil: true, aliniat: 'dreapta', valoare: (a) => a.implineste,
      randare: (a) => <span className="tnum">{a.implineste} ani</span> },
    { cheie: 'calatorii', antet: 'Calatorii', sortabil: true, aliniat: 'dreapta', valoare: (a) => a.calatorii,
      randare: (a) => (a.calatorii
        ? <span className="tnum">{a.calatorii}</span>
        : <span className="text-muted">—</span>) },
    { cheie: 'contact', antet: 'Contact',
      randare: (a) => (
        <span className="text-[13px] text-muted">{[a.telefon, a.email].filter(Boolean).join(' · ') || '—'}</span>
      ) },
    { cheie: 'actiuni', antet: '',
      randare: (a) => (
        <span className="whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          {a.telefon && (
            <>
              <a href={`tel:${a.telefon}`} className="text-[13px] text-accent-ink hover:underline mr-3">suna</a>
              <a href={`https://wa.me/${a.telefon.replace(/\D/g, '').replace(/^0/, '40')}?text=${encodeURIComponent(urare(a))}`}
                target="_blank" rel="noreferrer"
                className="text-[13px] text-accent-ink hover:underline mr-3">WhatsApp</a>
            </>
          )}
          {a.email && (
            <a href={`mailto:${a.email}?subject=${encodeURIComponent('La multi ani!')}&body=${encodeURIComponent(urare(a))}`}
              className="text-[13px] text-accent-ink hover:underline">email</a>
          )}
        </span>
      ) },
  ]

  return (
    <>
      <PageHeader title="Aniversari"
        count={data ? `${randuri.length} in urmatoarele ${zile} de zile · ${azi.length} azi` : undefined} />
      <div className="px-8 py-6 space-y-5">
        <div className="flex flex-wrap items-center gap-2">
          {FERESTRE.map((z) => (
            <Pill key={z} active={zile === z} onClick={() => setZile(z)}>
              {z === 7 ? 'saptamana asta' : `${z} de zile`}
            </Pill>
          ))}
          {acoperire.data && (
            <span className="ml-auto text-[12.5px] text-muted">
              stim ziua de nastere pentru{' '}
              <strong className="tnum">
                {(acoperire.data.din_camp + acoperire.data.din_cnp).toLocaleString('ro-RO')}
              </strong>{' '}
              din {acoperire.data.total_clienti.toLocaleString('ro-RO')} clienti
              {' '}({acoperire.data.din_cnp.toLocaleString('ro-RO')} dedusi din CNP)
            </span>
          )}
        </div>

        {azi.length > 0 && (
          <Card className="p-4 bg-good-soft border-good/30">
            <p className="text-[13.5px] text-good">
              Azi implinesc ani: {azi.map((a) => a.nume).join(', ')}.
            </p>
          </Card>
        )}

        <DataTable
          randuri={randuri} coloane={coloane} cheie={(a) => a.id} incarca={isPending}
          sortImplicit={{ cheie: 'cand', sens: 'asc' }}
          peRand={(a) => nav(`/contacte/${a.id}`)}
          cauta={(a, t) => `${a.nume} ${a.telefon ?? ''} ${a.email ?? ''} ${a.oras ?? ''}`.toLowerCase().includes(t)}
          placeholderCautare="Cauta client…"
          gol={{ titlu: 'Nicio aniversare in fereastra asta',
                 indiciu: 'Mareste fereastra sau completeaza datele de nastere pe clienti.' }}
        />
      </div>
    </>
  )
}
